import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { formatMoney, type SyncChange } from '@sp/shared'
import { useSignedIn } from '../session.tsx'
import { api } from '../lib/api.ts'
import { HttpError } from '../lib/http.ts'
import { useStores, useTrips } from '../lib/groupData.ts'
import { useDesktop } from '../lib/useDesktop.ts'
import { formatQty, parseSharedList, plural, storeSub, tripName, tripSub, type SharedEntry } from '../lib/format.ts'
import { saveCachedTrip } from '../lib/outbox.ts'
import { newId } from '../lib/storage.ts'
import { errorMessage } from '../lib/useResource.ts'
import {
  Button,
  Checkbox,
  EmptyState,
  PageTitle,
  Paragraph,
  SelectField,
  Stepper,
  TextCol,
} from '../components/ui.tsx'
import { DeskToolbar, Panel } from '../components/desk.tsx'

const NEW_TRIP = 'novy'

interface Draft extends SharedEntry {
  key: string
  checked: boolean
}

/**
 * Seznam sdileny do appky (Android „Sdílet" z Messengeru nebo poznamek) nebo
 * otevreny odkazem /sdilet?text=. Zadne LLM: radky, odrazky a „2x mléko" rozlozi
 * parseSharedList a clovek polozky pred pridanim zkontroluje.
 */
export function SharePage() {
  const s = useSignedIn()
  const groupId = s.group.id
  const navigate = useNavigate()
  const desktop = useDesktop()
  const [params] = useSearchParams()
  const trips = useTrips(groupId)
  const stores = useStores(groupId, true)

  // Poznamky (Keep) posilaji v title nazev poznamky — kdyz prisel i text, polozkou neni.
  const text = params.get('text')
  const shared = [text ?? params.get('title'), params.get('url')].filter(Boolean).join('\n')
  const [drafts, setDrafts] = useState<Draft[]>(() =>
    parseSharedList(shared).map((entry) => ({ ...entry, key: newId(), checked: true })),
  )
  const [targetChoice, setTargetChoice] = useState<string | null>(null)
  const [storeChoice, setStoreChoice] = useState<string | null>(null)
  /** Novy nakup zalozeny pri predchozim, nepovedenem pokusu — pri opakovani se nezaklada druhy. */
  const [createdTripId, setCreatedTripId] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const allStores = stores.res.state === 'ok' ? stores.res.data : []
  const open = (trips.res.state === 'ok' ? trips.res.data : []).filter((t) => t.status !== 'done')
  // Bez volby: rozpracovany bezny nakup, jinak novy.
  const targetId = targetChoice ?? open.find((t) => t.kind === 'quick')?.id ?? NEW_TRIP
  const target = open.find((t) => t.id === targetId) ?? null
  const route = target ? target.storeIds.flatMap((id) => allStores.find((store) => store.id === id) ?? []) : []
  const store = route.find((r) => r.id === storeChoice) ?? route[0] ?? null
  const chosen = drafts.filter((d) => d.checked)

  function update(key: string, patch: Partial<Draft>) {
    setDrafts((prev) => prev.map((d) => (d.key === key ? { ...d, ...patch } : d)))
  }

  async function submit() {
    if (chosen.length === 0) {
      setError('Vyber aspoň jednu položku')
      return
    }
    setBusy(true)
    setError(null)
    try {
      let tripId = target?.id ?? createdTripId
      if (!tripId) {
        const created = await api.trips.create(groupId, { kind: 'quick', title: 'Ze sdílení' })
        saveCachedTrip(created)
        setCreatedTripId(created.id)
        tripId = created.id
      }
      const changedAt = new Date().toISOString()
      const changes: SyncChange[] = chosen.map((d) => ({
        op: 'add',
        changedAt,
        item: {
          // Stejne id i pri opakovani: kdyz se ztratila jen odpoved, server pridani nezdvoji.
          id: d.key,
          name: d.name,
          catalogItemId: null,
          deal: null,
          storeId: store?.id ?? null,
          qty: d.qty,
          unit: d.unit,
          // Cena bez meny obchodu nedava smysl — u nakupu „kdekoli" se zahodi.
          expectedPriceMinor: store ? d.priceMinor : null,
          expectedCurrency: store?.currency ?? null,
          note: null,
          source: 'manual',
        },
      }))
      await api.trips.sync(tripId, changes)
      navigate(`/nakup/${tripId}`, { replace: true })
    } catch (err) {
      setError(err instanceof HttpError && err.isOffline ? 'Sdílený seznam jde přidat jen se signálem.' : errorMessage(err))
      setBusy(false)
    }
  }

  const list =
    drafts.length === 0 ? (
      <EmptyState title="Nic k přidání" action={<Button onClick={() => navigate('/')}>Zpět na nákupy</Button>}>
        Ve sdíleném textu nebyl žádný seznam. Pošli sem text s položkami na řádcích.
      </EmptyState>
    ) : (
      <div className="stack-8">
        {drafts.map((d) => (
          <div key={d.key} className={`select-row pick-row${d.checked ? ' is-selected' : ''}`}>
            <div className="pick-row-top">
              <button
                type="button"
                className="row-main"
                aria-pressed={d.checked}
                onClick={() => update(d.key, { checked: !d.checked })}
              >
                <Checkbox checked={d.checked} onRow />
                <TextCol
                  title={d.name}
                  sub={d.priceMinor !== null && store ? formatMoney(d.priceMinor, store.currency) : undefined}
                  subTone={d.checked ? 'fg' : 'muted'}
                />
              </button>
              {d.checked && (
                <Stepper
                  value={formatQty(d.qty, d.unit)}
                  decrementLabel={`Méně: ${d.name}`}
                  incrementLabel={`Více: ${d.name}`}
                  onDecrement={() => update(d.key, { qty: Math.max(1, d.qty - 1) })}
                  onIncrement={() => update(d.key, { qty: d.qty + 1 })}
                />
              )}
            </div>
          </div>
        ))}
      </div>
    )

  const controls = drafts.length > 0 && (
    <div className="stack">
      <SelectField
        label="Kam přidat"
        value={targetId}
        onChange={(value) => {
          setTargetChoice(value)
          setStoreChoice(null)
        }}
        options={[
          ...open.map((t) => ({
            value: t.id,
            label: tripName(t, allStores).title,
            sub: tripSub(t, allStores),
          })),
          { value: NEW_TRIP, label: 'Nový nákup', sub: 'pojmenuje se Ze sdílení' },
        ]}
      />
      {route.length > 1 && store && (
        <SelectField
          label="Obchod"
          value={store.id}
          onChange={setStoreChoice}
          options={route.map((r) => ({ value: r.id, label: r.name, sub: storeSub(r) }))}
        />
      )}
      {error && <Paragraph tone="error">{error}</Paragraph>}
      <div className="button-row">
        <Button onClick={() => navigate('/', { replace: true })}>Zrušit</Button>
        <Button kind="primary" className="grow" disabled={busy || chosen.length === 0} onClick={() => void submit()}>
          {chosen.length > 0 ? `Přidat ${plural(chosen.length, 'položku', 'položky', 'položek')}` : 'Nic není vybráno'}
        </Button>
      </div>
    </div>
  )

  if (desktop) {
    return (
      <div className="desk-view-main desk-narrow">
        <DeskToolbar title="Sdílený seznam" sub="Zkontroluj položky a vyber, kam je přidat." />
        <div className="desk-grid-2">
          <Panel title="Položky" meta={String(drafts.length)}>
            {list}
          </Panel>
          {controls && <Panel>{controls}</Panel>}
        </div>
      </div>
    )
  }

  return (
    <div className="app-shell">
      <div className="page">
        <main className="page-content">
          <PageTitle title="Sdílený seznam" subtitle="Zkontroluj položky a vyber, kam je přidat." />
          {list}
          {controls}
        </main>
      </div>
    </div>
  )
}
